import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import { User, Calendar, CalendarDays, Clock, Lock, Save, Check, LogOut, ChevronRight, ChevronLeft, ChevronDown, Search, Palette, Key, Eye, EyeOff, Copy, RefreshCw, Shield, Star, Trash2, AlertTriangle, X, Smartphone, KeyRound, Globe, PlayCircle, Bell, Mail, MessageSquare, Phone, Info, BarChart3, Crown, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEME_COLORS, BOTTOM_NAV_STYLES, UI_STYLES, DASHBOARD_STYLES } from '../context/ThemeContext';
import { useConfig } from '../context/ConfigContext';
import { useTransactions } from '../context/TransactionContext';
import { usePasscode } from '../context/PasscodeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useToast } from '../context/ToastContext';
import { authAPI } from '../services/api';
import { openExternalUrl } from '../services/native';
import {
  useNotificationPreferencesQuery,
  useUpdateNotificationPreferences,
  useSendPhoneVerification,
  useConfirmPhoneVerification,
  useRemovePhone
} from '../hooks/useQueries';
import { PHONE_CODE_LIST, getCountryByPhoneCode, getCountryByCode, getCountryByTimezone } from '../config/countries';
import Alert from '../components/common/Alert';
import ProfilePictureUpload from '../components/common/ProfilePictureUpload';

export default function SettingsPage() {
  const navigate = useNavigate();
  const params = useParams();
  const { user, updateProfile, logout, deleteAccount, refreshUser } = useAuth();
  const { theme, changeTheme, saving: themeSaving, bottomNavStyle, changeBottomNavStyle, uiStyle, changeUiStyle, isModernUI, dashboardStyle, changeDashboardStyle } = useTheme();
  const { formatDate } = useConfig();
  const { availableFinancialYears, fetchAvailableFinancialYears } = useTransactions();
  const { isPasscodeSet, isPasscodeEnabled } = usePasscode();
  const { isTrialing, isActive, isExpired, isPaused, isCancelled, trialDaysLeft, subscriptionDaysLeft, subscription, freeAccessMode, hidePaymentUI } = useSubscription();
  const { toast } = useToast();
  const { startOnboarding, isMobile } = useOnboarding();

  // Get section from URL path or default to 'profile'
  const activeTab = ['profile', 'theme', 'security', 'notifications'].includes(params.section)
    ? params.section
    : 'profile';

  // Navigate to a settings section
  const handleTabChange = (tab) => {
    navigate(`/settings/${tab}`, { replace: true });
  };

  // Fetch fresh user data and financial years on mount
  useEffect(() => {
    refreshUser();
    fetchAvailableFinancialYears();
  }, [fetchAvailableFinancialYears]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    financialYear: user?.financialYear || '2025-26',
    profilePicture: user?.profilePicture || null
  });

  // Phone state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedPhoneCode, setSelectedPhoneCode] = useState('+91');
  const [phoneCodeOpen, setPhoneCodeOpen] = useState(false);
  const [phoneCodeSearch, setPhoneCodeSearch] = useState('');
  const phoneCodeRef = useRef(null);
  const phoneCodeSearchRef = useRef(null);
  const [showCurrencyPrompt, setShowCurrencyPrompt] = useState(null); // { currency, countryName }
  const [pendingCurrencyUpdate, setPendingCurrencyUpdate] = useState(null);

  // Sync profileData when user changes (e.g., after page reload)
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        financialYear: user.financialYear || '2025-26',
        profilePicture: user.profilePicture || null
      });

      // Parse phone: "+919876543210" -> code "+91", number "9876543210"
      if (user.phone) {
        const match = PHONE_CODE_LIST.find(c => user.phone.startsWith(c.phoneCode));
        if (match) {
          setSelectedPhoneCode(match.phoneCode);
          setPhoneNumber(user.phone.slice(match.phoneCode.length));
        }
      } else {
        // Default phone code from timezone
        const countryCode = getCountryByTimezone();
        if (countryCode) {
          const country = getCountryByCode(countryCode);
          if (country) setSelectedPhoneCode(country.phoneCode);
        }
      }
    }
  }, [user]);

  // Encryption key state
  const [encryptionKeyData, setEncryptionKeyData] = useState({
    hasKey: user?.hasEncryptionKey || false,
    keySetAt: user?.encryptionKeySetAt || null,
    generatedKey: '',
    customKey: '',
    useCustomKey: false,
    showKey: false,
    viewPassword: '',
    viewedKey: ''
  });
  const [encryptionLoading, setEncryptionLoading] = useState(false);

  // Change password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showCurrentPassword: false,
    showNewPassword: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Set password state (for social auth users without password)
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [newPasswordData, setSetPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
    showPassword: false
  });

  // Delete account state
  const [deleteAccountData, setDeleteAccountData] = useState({
    showModal: false,
    password: '',
    showPassword: false,
    confirmText: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch encryption key status on mount
  useEffect(() => {
    const fetchKeyStatus = async () => {
      try {
        const { data } = await authAPI.getEncryptionKeyStatus();
        setEncryptionKeyData(prev => ({
          ...prev,
          hasKey: data.data.hasEncryptionKey,
          keySetAt: data.data.encryptionKeySetAt
        }));
      } catch (err) {
        console.error('Failed to fetch encryption key status:', err);
      }
    };
    fetchKeyStatus();
  }, []);

  // Close phone code dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (phoneCodeRef.current && !phoneCodeRef.current.contains(e.target)) {
        setPhoneCodeOpen(false);
        setPhoneCodeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (phoneCodeOpen && phoneCodeSearchRef.current) {
      phoneCodeSearchRef.current.focus();
    }
  }, [phoneCodeOpen]);

  // Filtered phone code list
  const filteredPhoneCodes = useMemo(() => {
    if (!phoneCodeSearch) return PHONE_CODE_LIST;
    const q = phoneCodeSearch.toLowerCase();
    return PHONE_CODE_LIST.filter(c =>
      c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)
    );
  }, [phoneCodeSearch]);

  const handlePhoneCodeChange = useCallback((newCode) => {
    setSelectedPhoneCode(newCode);
    setPhoneCodeOpen(false);
    setPhoneCodeSearch('');
    // Check if country's currency differs from user's current currency
    const newCountry = getCountryByPhoneCode(newCode);
    if (newCountry && user?.config?.currency !== newCountry.currency) {
      setShowCurrencyPrompt({ currency: newCountry.currency, countryName: newCountry.name });
      setPendingCurrencyUpdate(null); // Reset any previous acceptance
    } else {
      setShowCurrencyPrompt(null);
      setPendingCurrencyUpdate(null);
    }
  }, [user?.config?.currency]);

  const handleGenerateKey = async () => {
    try {
      setEncryptionLoading(true);
      const { data } = await authAPI.generateEncryptionKey();
      setEncryptionKeyData(prev => ({
        ...prev,
        generatedKey: data.data.key,
        useCustomKey: false
      }));
    } catch (err) {
      setError(err.error || 'Failed to generate key');
    } finally {
      setEncryptionLoading(false);
    }
  };

  const handleSaveEncryptionKey = async () => {
    const keyToSave = encryptionKeyData.useCustomKey
      ? encryptionKeyData.customKey
      : encryptionKeyData.generatedKey;

    if (!keyToSave || keyToSave.length < 32) {
      setError('Encryption key must be at least 32 characters');
      return;
    }

    try {
      setEncryptionLoading(true);
      await authAPI.setEncryptionKey(keyToSave);
      setEncryptionKeyData(prev => ({
        ...prev,
        hasKey: true,
        keySetAt: new Date().toISOString(),
        generatedKey: '',
        customKey: ''
      }));
      setSuccess('Encryption key saved successfully! Please save it in a secure location.');
    } catch (err) {
      setError(err.error || 'Failed to save encryption key');
    } finally {
      setEncryptionLoading(false);
    }
  };

  const handleViewKey = async () => {
    if (!encryptionKeyData.viewPassword) {
      setError('Please enter your password to view the key');
      return;
    }

    try {
      setEncryptionLoading(true);
      const { data } = await authAPI.viewEncryptionKey(encryptionKeyData.viewPassword);
      setEncryptionKeyData(prev => ({
        ...prev,
        viewedKey: data.data.encryptionKey,
        showKey: true
      }));
    } catch (err) {
      setError(err.error || 'Failed to view encryption key');
    } finally {
      setEncryptionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleProfilePictureUpload = async (uploadResult) => {
    // uploadResult contains { url, publicId } from Cloudinary
    setError('');
    setSuccess('');

    try {
      // Save the Cloudinary URL to user profile
      // Server returns the updated user with a cache-busted proxy URL
      const updatedUser = await updateProfile({ profilePicture: uploadResult.url });
      // Update local state with the proxy URL from server response
      setProfileData({ ...profileData, profilePicture: updatedUser.profilePicture });
      setSuccess('Profile picture updated!');
    } catch (err) {
      setError(err.message || 'Failed to update profile picture');
    }
  };

  const handleProfilePictureRemove = async () => {
    setError('');
    setSuccess('');

    try {
      // Use empty string to clear profilePicture in DB
      await updateProfile({ profilePicture: '' });
      setProfileData({ ...profileData, profilePicture: null });
      setSuccess('Profile picture removed!');
    } catch (err) {
      setError(err.message || 'Failed to remove profile picture');
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const phoneDigits = phoneNumber.replace(/\s/g, '');
      const phone = phoneDigits ? `${selectedPhoneCode}${phoneDigits}` : '';
      const { profilePicture: _pp, ...profileFields } = profileData;
      const payload = { ...profileFields, phone: phone || undefined };

      // If user accepted currency change
      if (pendingCurrencyUpdate) {
        payload.config = { ...user.config, currency: pendingCurrencyUpdate };
      }

      await updateProfile(payload);
      setShowCurrencyPrompt(null);
      setPendingCurrencyUpdate(null);
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!passwordData.currentPassword) {
      toast.error('Current password is required');
      return;
    }
    if (!passwordData.newPassword) {
      toast.error('New password is required');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');

    try {
      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast.success('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        showCurrentPassword: false,
        showNewPassword: false
      });
      setShowPasswordModal(false);
    } catch (err) {
      toast.error(err.error || err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Validate confirm text
    if (deleteAccountData.confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }

    if (!deleteAccountData.password) {
      setError('Please enter your password');
      return;
    }

    setDeleteLoading(true);
    setError('');

    try {
      await deleteAccount(deleteAccountData.password);
      // Redirect happens in AuthContext
    } catch (err) {
      setError(err.message || 'Failed to delete account');
      setDeleteLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteAccountData({
      showModal: false,
      password: '',
      showPassword: false,
      confirmText: ''
    });
    setError('');
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'theme', label: 'Theme', icon: Palette },
    ...(!user?.isAdmin ? [{ id: 'notifications', label: 'Notifications', icon: Bell }] : []),
    { id: 'security', label: 'Security', icon: Lock }
  ];

  return (
    <div className={`overflow-x-hidden ${isModernUI ? 'space-y-3' : 'space-y-6'}`}>
      {/* Mobile Back Header */}
      <StickyPageHeader className={`xl:hidden flex items-center gap-3 ${isModernUI ? 'mb-2' : 'mb-3'}`}>
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 -ml-1.5 rounded-lg text-surface-600 hover:bg-surface-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className={`font-semibold text-surface-900 ${isModernUI ? 'text-base' : 'text-lg'}`}>
            {tabs.find(t => t.id === activeTab)?.label || 'Settings'}
          </h1>
          <p className={`text-surface-500 ${isModernUI ? 'text-[10px]' : 'text-xs'}`}>Manage your account preferences</p>
        </div>
      </StickyPageHeader>

      {/* Desktop Header */}
      <div className="hidden xl:block page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Desktop Tabs Sidebar */}
        <div className="hidden xl:block lg:col-span-1">
          <nav className="bg-white rounded-2xl border border-surface-200 p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-left transition-all ${
                  activeTab === tab.id
                    ? 'font-medium'
                    : 'text-surface-600 hover:bg-surface-50'
                }`}
                style={activeTab === tab.id ? {
                  backgroundColor: 'var(--accent-bg)',
                  color: 'var(--accent-text)'
                } : {}}
              >
                <div className="flex items-center gap-3">
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                </div>
                {activeTab === tab.id && (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ))}
          </nav>

          {/* Logout Button - Desktop only in sidebar */}
          <button
            onClick={logout}
            className="hidden xl:flex w-full mt-4 items-center justify-center gap-2 px-4 py-3.5 bg-expense-50 text-expense-600 hover:bg-expense-100 rounded-xl font-medium transition-all"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 min-w-0">
          {activeTab === 'profile' && (
            <div className="space-y-3">

            {/* Avatar + Name Card */}
            <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
              <div className="flex items-center gap-3.5">
                <ProfilePictureUpload
                  value={profileData.profilePicture}
                  onUpload={handleProfilePictureUpload}
                  onRemove={handleProfilePictureRemove}
                  fallbackImage={user?.avatar}
                  userName={user?.name}
                  compact
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-bold text-surface-900 truncate">{user?.name || 'User'}</p>
                  <p className="text-[11px] text-surface-500 truncate">{user?.email || ''}</p>
                  {/* Subscription badge — hidden for admin */}
                  {!freeAccessMode && !hidePaymentUI && !user?.isAdmin && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <Crown className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-[10px] font-semibold" style={{ color: 'var(--accent-bg)' }}>
                        {isTrialing ? 'Free Plan' : isActive ? 'Active' : isExpired ? 'Expired' : isPaused ? 'Paused' : 'Free Plan'}
                      </span>
                      {(!isActive || isTrialing) && (
                        <button
                          onClick={() => navigate('/pricing')}
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-md text-white"
                          style={{ backgroundColor: 'var(--accent-bg)' }}
                        >
                          Upgrade
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Settings Form */}
            <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4" style={{ color: 'var(--accent-bg)' }} />
                <span className="text-[13px] font-bold text-surface-900">Profile Settings</span>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-3">
                {/* Full Name */}
                <div>
                  <label className="block text-[10px] font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                    <input
                      type="text"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                    />
                  </div>
                </div>

                {/* Email (locked) */}
                <div>
                  <label className="block text-[10px] font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Email</label>
                  <div className="flex items-center gap-2.5 px-3 py-2.5 bg-surface-50 border border-surface-200 rounded-xl">
                    <Mail className="w-4 h-4 text-surface-400 flex-shrink-0" />
                    <span className="text-sm text-surface-500 flex-1 min-w-0 truncate">{user?.email || ''}</span>
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-surface-200 text-surface-500 flex-shrink-0">Locked</span>
                  </div>
                  <p className="text-[10px] text-surface-400 mt-1">Email cannot be changed</p>
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-[10px] font-semibold text-surface-500 uppercase tracking-wide mb-1.5">Phone Number</label>
                  <div className="flex gap-1.5">
                    <div className="relative shrink-0" ref={phoneCodeRef}>
                      <button
                        type="button"
                        onClick={() => setPhoneCodeOpen(!phoneCodeOpen)}
                        className="flex items-center gap-0.5 bg-white border border-surface-200 rounded-xl text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all whitespace-nowrap px-2.5 py-2.5 text-sm"
                      >
                        <span className="font-medium text-sm">{selectedPhoneCode}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-surface-400" />
                      </button>
                      {phoneCodeOpen && (
                        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-surface-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-surface-100">
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
                              <input
                                ref={phoneCodeSearchRef}
                                type="text"
                                value={phoneCodeSearch}
                                onChange={(e) => setPhoneCodeSearch(e.target.value)}
                                placeholder="Search country..."
                                className="w-full pl-9 pr-3 py-2 text-sm bg-surface-50 border border-surface-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-surface-900"
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-48">
                            {filteredPhoneCodes.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => handlePhoneCodeChange(c.phoneCode)}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-surface-50 transition-colors flex items-center justify-between ${
                                  selectedPhoneCode === c.phoneCode ? 'bg-surface-50 font-medium' : ''
                                }`}
                              >
                                <span className="truncate">{c.name}</span>
                                <span className="text-surface-500 ml-2 shrink-0">{c.phoneCode}</span>
                              </button>
                            ))}
                            {filteredPhoneCodes.length === 0 && (
                              <p className="px-3 py-2 text-sm text-surface-400">No countries found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="relative flex-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d]/g, ''))}
                        placeholder="9876543210"
                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Currency prompt */}
                  {showCurrencyPrompt && (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl mt-2 px-3 py-2">
                      <p className="text-blue-800 text-xs">
                        Your currency is <span className="font-semibold">{user?.config?.currency}</span> but {showCurrencyPrompt.countryName} uses <span className="font-semibold">{showCurrencyPrompt.currency}</span>. Update?
                      </p>
                      <div className="flex items-center gap-2 ml-3 shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setPendingCurrencyUpdate(showCurrencyPrompt.currency);
                            setShowCurrencyPrompt(null);
                          }}
                          className="font-medium text-blue-700 hover:text-blue-900 text-xs"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCurrencyPrompt(null);
                            setPendingCurrencyUpdate(null);
                          }}
                          className="font-medium text-surface-500 hover:text-surface-700 text-xs"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}
                  {pendingCurrencyUpdate && !showCurrencyPrompt && (
                    <p className="text-green-600 text-[10px] mt-1.5">
                      Currency will update to {pendingCurrencyUpdate} on save
                    </p>
                  )}
                </div>

                {/* Save Button */}
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Legal Links */}
            <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
              <div className="flex items-center gap-2 mb-1">
                <Info className="w-3.5 h-3.5 text-surface-500" />
                <span className="text-[13px] font-bold text-surface-900">Legal</span>
              </div>
              <button
                onClick={() => openExternalUrl('https://hisabjourney.com/privacy')}
                className="w-full flex items-center justify-between py-3 border-b border-surface-100"
              >
                <div className="flex items-center gap-2.5">
                  <Lock className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-[13px] font-medium text-surface-700">Privacy Policy</span>
                </div>
                <ChevronRight className="w-4 h-4 text-surface-300" />
              </button>
              <button
                onClick={() => openExternalUrl('https://hisabjourney.com/terms')}
                className="w-full flex items-center justify-between py-3"
              >
                <div className="flex items-center gap-2.5">
                  <Shield className="w-3.5 h-3.5 text-purple-500" />
                  <span className="text-[13px] font-medium text-surface-700">Terms of Service</span>
                </div>
                <ChevronRight className="w-4 h-4 text-surface-300" />
              </button>
            </div>

            {/* Logout is in More menu (BottomNav) on mobile */}

            {/* Delete Account */}
            <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
              <button
                onClick={() => setDeleteAccountData(prev => ({ ...prev, showModal: true }))}
                className="w-full flex items-center justify-between py-1"
              >
                <div className="flex items-center gap-2.5">
                  <Trash2 className="w-3.5 h-3.5 text-expense-500" />
                  <span className="text-[13px] font-medium text-expense-600">Delete Account</span>
                </div>
                <ChevronRight className="w-4 h-4 text-surface-300" />
              </button>
            </div>

            </div>
          )}

          {activeTab === 'theme' && (
            <div className="space-y-4">
              {/* Theme Colors Card */}
              <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg">🎨</span>
                    <div>
                      <h3 className="text-sm font-bold text-surface-900">Theme</h3>
                      <p className="text-[11px] text-surface-500">Personalise your colour scheme</p>
                    </div>
                  </div>
                  {themeSaving && (
                    <span className="text-xs text-surface-500 flex items-center gap-1.5">
                      <span
                        className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: 'var(--accent-bg)', borderTopColor: 'transparent' }}
                      />
                      Saving...
                    </span>
                  )}
                </div>

                {/* Current theme preview bar */}
                <div
                  className="rounded-xl p-2.5 flex items-center gap-2.5 mb-4"
                  style={{ backgroundColor: 'var(--sidebar-bg)' }}
                >
                  <div
                    className="w-5 h-5 rounded flex items-center justify-center"
                    style={{ backgroundColor: 'var(--accent-bg)' }}
                  >
                    <span className="text-[6px] font-extrabold text-white">HJ</span>
                  </div>
                  <div className="flex-1 h-1 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.3 }} />
                  <div className="flex gap-1.5">
                    <div className="w-6 h-4 rounded-md" style={{ backgroundColor: 'var(--accent-bg)', opacity: 0.3 }} />
                    <div className="w-6 h-4 rounded-md" style={{ backgroundColor: 'var(--accent-bg)', opacity: 0.5 }} />
                  </div>
                </div>

                {/* Recommended */}
                <div className="flex items-center gap-1.5 mb-2">
                  <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--accent-bg)' }}>Recommended</span>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {['pink', 'slate', 'zinc', 'nature', 'mint'].map((key) => {
                    const t = THEME_COLORS[key];
                    return (
                      <button
                        key={key}
                        onClick={() => changeTheme(key)}
                        className={`relative rounded-xl border-2 overflow-hidden transition-all ${
                          theme === key ? 'border-surface-900 shadow-md' : 'border-surface-200'
                        }`}
                      >
                        {/* Mini sidebar preview */}
                        <div className="p-1.5" style={{ backgroundColor: t.sidebar.bg }}>
                          <div className="flex items-center gap-1 mb-1">
                            <div className="w-3 h-3 rounded flex items-center justify-center" style={{ backgroundColor: t.accent.bg }}>
                              <span className="text-[5px] font-extrabold text-white">HJ</span>
                            </div>
                            <div className="h-[3px] w-4 rounded" style={{ backgroundColor: t.sidebar.text, opacity: 0.5 }} />
                          </div>
                          <div className="h-2 rounded px-1 flex items-center mb-0.5" style={{ backgroundColor: t.accent.bg }}>
                            <div className="h-[2px] w-3 rounded bg-white/70" />
                          </div>
                          <div className="h-[3px] w-5 rounded" style={{ backgroundColor: t.sidebar.text, opacity: 0.4 }} />
                        </div>
                        {/* Name */}
                        <div className="text-center py-1 bg-white">
                          <span className="text-[10px] font-medium text-surface-700">{t.name}</span>
                        </div>
                        {theme === key && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: t.accent.bg }}>
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* More Themes */}
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: 'var(--accent-bg)' }}>More Themes</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(THEME_COLORS)
                    .filter(([key]) => !['pink', 'slate', 'zinc', 'nature', 'mint'].includes(key))
                    .sort(([a], [b]) => a === 'minimal' ? -1 : b === 'minimal' ? 1 : 0)
                    .map(([key, t]) => (
                      <button
                        key={key}
                        onClick={() => changeTheme(key)}
                        className={`relative rounded-xl border-2 overflow-hidden transition-all ${
                          theme === key ? 'border-surface-900 shadow-md' : 'border-surface-200'
                        }`}
                      >
                        <div className="p-1.5" style={{ backgroundColor: t.sidebar.bg }}>
                          <div className="flex items-center gap-1 mb-1">
                            <div className="w-3 h-3 rounded flex items-center justify-center" style={{ backgroundColor: t.accent.bg }}>
                              <span className="text-[5px] font-extrabold text-white">HJ</span>
                            </div>
                            <div className="h-[3px] w-4 rounded" style={{ backgroundColor: t.sidebar.text, opacity: 0.5 }} />
                          </div>
                          <div className="h-2 rounded px-1 flex items-center mb-0.5" style={{ backgroundColor: t.accent.bg }}>
                            <div className="h-[2px] w-3 rounded bg-white/70" />
                          </div>
                          <div className="h-[3px] w-5 rounded" style={{ backgroundColor: t.sidebar.text, opacity: 0.4 }} />
                        </div>
                        <div className="text-center py-1 bg-white">
                          <span className="text-[10px] font-medium text-surface-700">{t.name}</span>
                        </div>
                        {theme === key && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: t.accent.bg }}>
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                </div>
              </div>

              {/* Navigation Style Card */}
              <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-surface-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-surface-900">Navigation Style</h3>
                    <p className="text-[11px] text-surface-500">How the bottom bar appears</p>
                  </div>
                </div>

                <div className="space-y-2.5">
                  {Object.entries(BOTTOM_NAV_STYLES).map(([key, style]) => (
                    <button
                      key={key}
                      onClick={() => changeBottomNavStyle(key)}
                      className={`relative w-full rounded-xl border-2 p-3 text-left transition-all ${
                        bottomNavStyle === key
                          ? 'border-surface-900 shadow-sm'
                          : 'border-surface-200'
                      }`}
                      style={bottomNavStyle === key ? { backgroundColor: 'var(--accent-light)' } : {}}
                    >
                      {/* Nav bar preview */}
                      <div className={`w-full h-9 rounded-lg flex justify-evenly items-center mb-2 ${
                        key === 'floating' || key === 'floatingClassic' ? 'rounded-xl' : 'border border-surface-200 bg-surface-50'
                      }`}
                        style={
                          key === 'floating' || key === 'floatingClassic'
                            ? { backgroundColor: 'var(--sidebar-bg)' }
                            : {}
                        }
                      >
                        {key === 'classic' && (
                          <>
                            <div className="w-3.5 h-3.5 rounded bg-surface-300" />
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--accent-bg)' }} />
                            <div className="w-3.5 h-3.5 rounded bg-surface-300" />
                            <div className="w-3.5 h-3.5 rounded bg-surface-300" />
                          </>
                        )}
                        {key === 'pill' && (
                          <>
                            <div className="w-3.5 h-3.5 rounded bg-surface-300" />
                            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--accent-bg)' }}>
                              <div className="w-2.5 h-2.5 rounded bg-white/70" />
                              <div className="w-4 h-1 rounded bg-white/70" />
                            </div>
                            <div className="w-3.5 h-3.5 rounded bg-surface-300" />
                            <div className="w-3.5 h-3.5 rounded bg-surface-300" />
                          </>
                        )}
                        {key === 'floating' && (
                          <>
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.5 }} />
                            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--accent-bg)' }}>
                              <div className="w-2.5 h-2.5 rounded bg-white/70" />
                              <div className="w-4 h-1 rounded bg-white/70" />
                            </div>
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.5 }} />
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.5 }} />
                          </>
                        )}
                        {key === 'floatingClassic' && (
                          <>
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.5 }} />
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--accent-bg)' }} />
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.5 }} />
                            <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: 'var(--sidebar-text)', opacity: 0.5 }} />
                          </>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-semibold text-surface-900">{style.name}</div>
                          <div className="text-[10px] text-surface-500">{style.description}</div>
                        </div>
                        {bottomNavStyle === key && (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-bg)' }}>
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* App Tutorial Card */}
              {isMobile && (
                <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                      <PlayCircle className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-surface-900">App Tutorial</h3>
                      <p className="text-[11px] text-surface-500">Replay hidden features walkthrough</p>
                    </div>
                    <button
                      onClick={() => {
                        startOnboarding();
                        navigate('/');
                      }}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white flex-shrink-0"
                      style={{ backgroundColor: 'var(--accent-bg)' }}
                    >
                      Replay
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notifications' && (
            <NotificationsTab isModernUI={isModernUI} toast={toast} />
          )}

          {activeTab === 'security' && (
            <div className="space-y-4">
              {/* Password Card */}
              <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                {/* Header */}
                <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-surface-100">
                  <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-surface-900">Password</h3>
                    <p className="text-[11px] text-surface-500">
                      {user?.needsPasswordSetup ? 'Not set yet' : 'Password is set'}
                    </p>
                  </div>
                </div>
                {/* Set or Change Password row */}
                <button
                  onClick={() => user?.needsPasswordSetup ? setShowSetPasswordModal(true) : setShowPasswordModal(true)}
                  className="w-full flex items-center justify-between py-1 text-left"
                >
                  <div>
                    <div className="text-[13px] font-semibold text-surface-900">
                      {user?.needsPasswordSetup ? 'Set Password' : 'Change Password'}
                    </div>
                    <div className="text-[11px] text-surface-500">
                      {user?.needsPasswordSetup
                        ? 'Add a password to also login with email'
                        : 'Update to keep your account secure'}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-surface-400" />
                </button>
              </div>

              {/* Biometric Auth Card — commented out, will add later */}
              {/* <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-surface-100">
                  <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-surface-900">Biometric Auth</h3>
                  </div>
                </div>
                <div className="flex items-center justify-between py-1">
                  <div>
                    <div className="text-[13px] font-semibold text-surface-900">Face ID / Fingerprint</div>
                    <div className="text-[11px] text-surface-500">Quick secure login using biometrics</div>
                  </div>
                  <div className="w-10 h-[22px] rounded-full bg-surface-200 relative cursor-pointer">
                    <div className="w-[18px] h-[18px] rounded-full bg-white absolute top-[2px] left-[2px] shadow-sm" />
                  </div>
                </div>
              </div> */}

              {/* App Lock Card */}
              <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-surface-100">
                  <div className="w-8 h-8 rounded-xl bg-yellow-50 flex items-center justify-center">
                    <KeyRound className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-surface-900">App Lock</h3>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/passcode')}
                  className="w-full flex items-center justify-between py-1 text-left"
                >
                  <div>
                    <div className="text-[13px] font-semibold text-surface-900">Set Passcode</div>
                    <div className="text-[11px] text-surface-500">
                      {isPasscodeSet
                        ? isPasscodeEnabled
                          ? '4 or 6 digit PIN lock — Enabled'
                          : '4 or 6 digit PIN lock — Disabled'
                        : '4 or 6 digit PIN lock'}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-surface-400" />
                </button>
              </div>

              {/* Account Information Card */}
              <div className="bg-white rounded-2xl border border-surface-200 p-3.5">
                <div className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--accent-bg)' }}>
                  Account Information
                </div>
                <div className="space-y-0">
                  <div className="flex items-center gap-2.5 py-2 border-b border-surface-50">
                    <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
                      <CalendarDays className="w-3.5 h-3.5 text-surface-500" />
                    </div>
                    <span className="flex-1 text-xs text-surface-500">Account Created</span>
                    <span className="text-xs font-semibold text-surface-900">
                      {user?.createdAt ? formatDate(user.createdAt) : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 py-2 border-b border-surface-50">
                    <div className="w-7 h-7 rounded-lg bg-surface-100 flex items-center justify-center">
                      <Clock className="w-3.5 h-3.5 text-surface-500" />
                    </div>
                    <span className="flex-1 text-xs text-surface-500">Last Login</span>
                    <span className="text-xs font-semibold text-surface-900">
                      {user?.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 py-2">
                    <div className="w-7 h-7 rounded-lg bg-income-50 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-income-600" />
                    </div>
                    <span className="flex-1 text-xs text-surface-500">Account Status</span>
                    <span className="text-xs font-semibold text-income-600">Active</span>
                  </div>
                </div>
              </div>

              {/* Encryption Key Section - Hidden: Auto-generated during registration
                          Encryption key is set
                        </span>
                        {encryptionKeyData.keySetAt && (
                          <span className="text-xs text-income-600 ml-auto">
                            Set on {new Date(encryptionKeyData.keySetAt).toLocaleDateString('en-IN')}
                          </span>
                        )}
                      </div>

                      <div className="border-t border-surface-200 pt-4">
                        <p className="text-sm text-surface-600 mb-3">
                          Enter your password to view your encryption key:
                        </p>
                        <div className="flex gap-3">
                          <div className="relative flex-1">
                            <input
                              type={encryptionKeyData.showKey ? 'text' : 'password'}
                              value={encryptionKeyData.viewPassword}
                              onChange={(e) => setEncryptionKeyData(prev => ({ ...prev, viewPassword: e.target.value }))}
                              placeholder="Enter your password"
                              className="w-full px-4 py-2.5 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <button
                            onClick={handleViewKey}
                            disabled={encryptionLoading}
                            className="px-4 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50"
                            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                          >
                            {encryptionLoading ? 'Loading...' : 'View Key'}
                          </button>
                        </div>

                        {encryptionKeyData.viewedKey && (
                          <div className="mt-4 p-4 bg-surface-100 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-surface-700">Your Encryption Key:</span>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEncryptionKeyData(prev => ({ ...prev, showKey: !prev.showKey }))}
                                  className="p-2 text-surface-500 hover:text-surface-700 rounded-lg hover:bg-surface-200"
                                >
                                  {encryptionKeyData.showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                                <button
                                  onClick={() => copyToClipboard(encryptionKeyData.viewedKey)}
                                  className="p-2 text-surface-500 hover:text-surface-700 rounded-lg hover:bg-surface-200"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <code className="block p-3 bg-surface-900 text-surface-100 rounded-lg text-sm font-mono break-all">
                              {encryptionKeyData.showKey ? encryptionKeyData.viewedKey : '•'.repeat(64)}
                            </code>
                            <p className="text-xs text-expense-600 mt-2">
                              Keep this key safe! If you lose it, you will not be able to decrypt your data.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                        <Shield className="w-5 h-5 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">
                          Encryption key not set - Please set up your key to secure your data
                        </span>
                      </div>

                      <div className="space-y-4">
                        <div className="flex gap-3">
                          <button
                            onClick={handleGenerateKey}
                            disabled={encryptionLoading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-surface-200 text-surface-700 hover:bg-surface-50 rounded-xl font-medium transition-all disabled:opacity-50"
                          >
                            <RefreshCw className={`w-5 h-5 ${encryptionLoading ? 'animate-spin' : ''}`} />
                            Generate Secure Key
                          </button>
                          <button
                            onClick={() => setEncryptionKeyData(prev => ({ ...prev, useCustomKey: !prev.useCustomKey, generatedKey: '' }))}
                            className={`flex-1 px-4 py-3 border-2 rounded-xl font-medium transition-all ${
                              encryptionKeyData.useCustomKey
                                ? 'border-primary-500 bg-primary-50 text-primary-700'
                                : 'border-surface-200 text-surface-700 hover:bg-surface-50'
                            }`}
                          >
                            Use Custom Key
                          </button>
                        </div>

                        {encryptionKeyData.generatedKey && !encryptionKeyData.useCustomKey && (
                          <div className="p-4 bg-surface-100 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-surface-700">Generated Key:</span>
                              <button
                                onClick={() => copyToClipboard(encryptionKeyData.generatedKey)}
                                className="p-2 text-surface-500 hover:text-surface-700 rounded-lg hover:bg-surface-200"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                            <code className="block p-3 bg-surface-900 text-surface-100 rounded-lg text-sm font-mono break-all">
                              {encryptionKeyData.generatedKey}
                            </code>
                            <p className="text-xs text-expense-600 mt-2">
                              Copy and save this key in a secure location before saving!
                            </p>
                          </div>
                        )}

                        {encryptionKeyData.useCustomKey && (
                          <div>
                            <label className="block text-sm font-medium text-surface-700 mb-2">
                              Enter your custom encryption key (min 32 characters):
                            </label>
                            <input
                              type="text"
                              value={encryptionKeyData.customKey}
                              onChange={(e) => setEncryptionKeyData(prev => ({ ...prev, customKey: e.target.value }))}
                              placeholder="Enter at least 32 characters..."
                              className="w-full px-4 py-3 border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                            />
                            <p className="text-xs text-surface-500 mt-1">
                              {encryptionKeyData.customKey.length}/32 characters (minimum)
                            </p>
                          </div>
                        )}

                        {(encryptionKeyData.generatedKey || (encryptionKeyData.useCustomKey && encryptionKeyData.customKey.length >= 32)) && (
                          <button
                            onClick={handleSaveEncryptionKey}
                            disabled={encryptionLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                          >
                            <Save className="w-5 h-5" />
                            {encryptionLoading ? 'Saving...' : 'Save Encryption Key'}
                          </button>
                        )}

                        <p className="text-xs text-surface-500 p-3 bg-amber-50 rounded-lg">
                          <strong className="text-amber-700">Important:</strong> This key can only be set once and cannot be changed later.
                          Make sure to save it in a secure location. If you lose this key, you will not be able to access your encrypted data.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                End of Encryption Key Section */}

            </div>
          )}

          {/* Change Password Modal */}
          {showPasswordModal && (
            <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '', showCurrentPassword: false, showNewPassword: false });
                }}
              />
              {/* Modal */}
              <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-5 pb-8">
                {/* Drag handle (mobile) */}
                <div className="sm:hidden flex justify-center mb-3">
                  <div className="w-10 h-1 rounded-full bg-surface-300" />
                </div>
                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-amber-600" />
                    </div>
                    <h3 className="text-base font-bold text-surface-900">Change Password</h3>
                  </div>
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '', showCurrentPassword: false, showNewPassword: false });
                    }}
                    className="p-1.5 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-3.5">
                  {/* Current Password */}
                  <div>
                    <label className="block text-xs font-medium text-surface-700 mb-1.5">CURRENT PASSWORD</label>
                    <div className="relative">
                      <input
                        type={passwordData.showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter current password"
                        className="w-full bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 px-3 py-2.5 pr-10 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setPasswordData(prev => ({ ...prev, showCurrentPassword: !prev.showCurrentPassword }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600"
                      >
                        {passwordData.showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className="block text-xs font-medium text-surface-700 mb-1.5">NEW PASSWORD</label>
                    <div className="relative">
                      <input
                        type={passwordData.showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Min 6 characters"
                        className="w-full bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 px-3 py-2.5 pr-10 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setPasswordData(prev => ({ ...prev, showNewPassword: !prev.showNewPassword }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600"
                      >
                        {passwordData.showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-xs font-medium text-surface-700 mb-1.5">CONFIRM PASSWORD</label>
                    <input
                      type={passwordData.showNewPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Confirm new password"
                      className="w-full bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 px-3 py-2.5 text-sm"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="w-full flex items-center justify-center gap-2 rounded-xl font-semibold text-sm py-2.5 transition-all disabled:opacity-50"
                    style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                  >
                    {passwordLoading ? 'Changing...' : 'Change Password'}
                  </button>
                </form>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Set Password Modal (for social auth users without password) */}
      {showSetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-surface-900">Set Password</h3>
              <button
                onClick={() => {
                  setShowSetPasswordModal(false);
                  setSetPasswordData({ newPassword: '', confirmPassword: '', showPassword: false });
                }}
                className="text-surface-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-surface-500">Set a password so you can also login with your email address.</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!newPasswordData.newPassword || newPasswordData.newPassword.length < 8) {
                toast.error('Password must be at least 8 characters');
                return;
              }
              if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
                toast.error('Passwords do not match');
                return;
              }
              setPasswordLoading(true);
              try {
                const { data } = await authAPI.setPassword({
                  password: newPasswordData.newPassword,
                  confirmPassword: newPasswordData.confirmPassword
                });
                toast.success('Password set successfully!');
                setShowSetPasswordModal(false);
                setSetPasswordData({ newPassword: '', confirmPassword: '', showPassword: false });
                // Update user in state so UI reflects password is now set
                if (data.data?.token) {
                  localStorage.setItem('token', data.data.token);
                }
                refreshUser();
              } catch (err) {
                toast.error(err.error || 'Failed to set password');
              } finally {
                setPasswordLoading(false);
              }
            }} className="space-y-3.5">
              <div>
                <label className="block text-xs font-medium text-surface-700 mb-1.5">NEW PASSWORD</label>
                <div className="relative">
                  <input
                    type={newPasswordData.showPassword ? 'text' : 'password'}
                    value={newPasswordData.newPassword}
                    onChange={(e) => setSetPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Min 8 characters"
                    className="w-full bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 px-3 py-2.5 text-sm pr-10"
                  />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400"
                    onClick={() => setSetPasswordData(prev => ({ ...prev, showPassword: !prev.showPassword }))}>
                    {newPasswordData.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-700 mb-1.5">CONFIRM PASSWORD</label>
                <input
                  type={newPasswordData.showPassword ? 'text' : 'password'}
                  value={newPasswordData.confirmPassword}
                  onChange={(e) => setSetPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Re-enter password"
                  className="w-full bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 px-3 py-2.5 text-sm"
                />
              </div>
              <button type="submit" disabled={passwordLoading}
                className="w-full flex items-center justify-center gap-2 rounded-xl font-semibold text-sm py-2.5 transition-all disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}>
                {passwordLoading ? 'Setting...' : 'Set Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation — Mobile Bottom Sheet + Desktop Modal */}
      {deleteAccountData.showModal && (
        <>
          {/* Mobile Bottom Sheet */}
          <div
            className="xl:hidden fixed inset-0 z-[60] flex items-end"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
            onClick={closeDeleteModal}
          >
            <div
              className="w-full bg-white rounded-t-3xl animate-slide-up safe-area-bottom"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle + Close */}
              <div className="flex justify-center pt-3 pb-1 relative">
                <div className="w-10 h-1 bg-surface-300 rounded-full" />
                <button
                  onClick={closeDeleteModal}
                  className="absolute right-4 top-2 w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center text-surface-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 pt-3 pb-8 space-y-4" style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}>
                {/* Icon + Title */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-2xl bg-expense-100 flex items-center justify-center mb-3">
                    <AlertTriangle className="w-7 h-7 text-expense-600" />
                  </div>
                  <h3 className="text-lg font-bold text-surface-900">Delete Account</h3>
                  <p className="text-sm text-surface-500 mt-1.5 leading-relaxed max-w-[300px]">
                    This is <strong className="text-expense-600">irreversible</strong>. All your data will be permanently deleted.
                  </p>
                </div>

                {error && (
                  <Alert variant="error" message={error} onClose={() => setError('')} />
                )}

                {/* Password */}
                <div>
                  <label className="block text-xs font-medium text-surface-500 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={deleteAccountData.showPassword ? 'text' : 'password'}
                      value={deleteAccountData.password}
                      onChange={(e) => setDeleteAccountData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Enter your password"
                      className="input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setDeleteAccountData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-surface-400"
                    >
                      {deleteAccountData.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm text */}
                <div>
                  <label className="block text-xs font-medium text-surface-500 mb-1.5">
                    Type <span className="font-bold text-expense-600">DELETE</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteAccountData.confirmText}
                    onChange={(e) => setDeleteAccountData(prev => ({ ...prev, confirmText: e.target.value }))}
                    placeholder="DELETE"
                    className="input"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-2.5 pt-1">
                  <button
                    onClick={closeDeleteModal}
                    disabled={deleteLoading}
                    className="flex-1 py-3.5 rounded-2xl font-semibold text-[15px] text-surface-600 bg-surface-100 hover:bg-surface-200 transition-all active:scale-[0.98]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || deleteAccountData.confirmText !== 'DELETE' || !deleteAccountData.password}
                    className="flex-1 py-3.5 rounded-2xl font-semibold text-[15px] text-white bg-expense-600 hover:bg-expense-700 transition-all disabled:opacity-40 active:scale-[0.98] inline-flex items-center justify-center gap-2"
                  >
                    {deleteLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Modal */}
          <div className="hidden xl:flex modal-overlay z-[60]" onClick={closeDeleteModal}>
            <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-expense-100 flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-expense-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-surface-900 mb-1">Delete Account</h3>
                    <p className="text-sm text-surface-600">
                      This is <strong className="text-expense-600">irreversible</strong>. All your data will be permanently deleted.
                    </p>
                  </div>
                </div>

                {error && (
                  <Alert variant="error" message={error} onClose={() => setError('')} />
                )}

                <div>
                  <label className="block text-xs font-medium text-surface-500 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={deleteAccountData.showPassword ? 'text' : 'password'}
                      value={deleteAccountData.password}
                      onChange={(e) => setDeleteAccountData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Enter your password"
                      className="input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setDeleteAccountData(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-surface-400"
                    >
                      {deleteAccountData.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-surface-500 mb-1.5">
                    Type <span className="font-bold text-expense-600">DELETE</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteAccountData.confirmText}
                    onChange={(e) => setDeleteAccountData(prev => ({ ...prev, confirmText: e.target.value }))}
                    placeholder="DELETE"
                    className="input"
                  />
                </div>

                <div className="flex gap-3 justify-end pt-1">
                  <button
                    onClick={closeDeleteModal}
                    disabled={deleteLoading}
                    className="px-4 py-2.5 border-2 border-surface-200 text-surface-700 hover:bg-surface-50 rounded-xl font-medium transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || deleteAccountData.confirmText !== 'DELETE' || !deleteAccountData.password}
                    className="px-4 py-2.5 rounded-xl font-medium text-white bg-expense-600 hover:bg-expense-700 transition-all disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    {deleteLoading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Account'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Notifications Tab Component
function NotificationsTab({ isModernUI, toast }) {
  const { data, isLoading, error } = useNotificationPreferencesQuery();
  const updatePreferences = useUpdateNotificationPreferences();
  const sendVerification = useSendPhoneVerification();
  const confirmVerification = useConfirmPhoneVerification();
  const removePhone = useRemovePhone();

  const [phoneInput, setPhoneInput] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Local state for preferences (optimistic updates)
  const [localPrefs, setLocalPrefs] = useState(null);

  // Sync local state with server data
  useEffect(() => {
    if (data?.preferences) {
      setLocalPrefs(data.preferences);
    }
  }, [data]);

  const preferences = localPrefs || data?.preferences;
  const phoneInfo = data?.phone;
  const smsEnabled = data?.smsEnabled || data?.whatsappEnabled; // Show phone section if either is enabled

  // Summary notification types
  const summaryTypes = [
    {
      key: 'weeklySummary',
      label: 'Weekly Summary',
      description: 'Receive a summary of your finances every Sunday',
      icon: Calendar,
      defaultEnabled: false // Weekly is disabled by default
    },
    {
      key: 'monthlySummary',
      label: 'Monthly Summary',
      description: 'Receive a summary of your finances at the start of each month',
      icon: BarChart3,
      defaultEnabled: true // Monthly is enabled by default
    }
  ];

  // Check if a summary type is enabled (has 'email' in channels)
  const isSummaryEnabled = (key) => {
    const channels = preferences?.preferences?.[key] || [];
    return channels.includes('email');
  };

  // Toggle summary on/off
  const handleToggleSummary = async (key) => {
    const currentlyEnabled = isSummaryEnabled(key);
    // When enabling, set to ['email']; when disabling, set to empty array
    const newChannels = currentlyEnabled ? [] : ['email'];

    // Optimistic update
    setLocalPrefs(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [key]: newChannels
      }
    }));

    try {
      await updatePreferences.mutateAsync({
        preferences: {
          [key]: newChannels
        }
      });
      const label = key === 'weeklySummary' ? 'Weekly summary' : 'Monthly summary';
      toast.success(currentlyEnabled ? `${label} disabled` : `${label} enabled`);
    } catch {
      // Revert on error
      setLocalPrefs(data?.preferences);
      toast.error('Failed to update preferences');
    }
  };

  const handleSendVerification = async () => {
    setPhoneError('');

    // Basic phone validation
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    if (!phoneRegex.test(phoneInput)) {
      setPhoneError('Enter a valid phone number with country code (e.g., +919876543210)');
      return;
    }

    try {
      await sendVerification.mutateAsync(phoneInput);
      setShowOtpInput(true);
      toast.success('Verification code sent to your phone');
    } catch (err) {
      setPhoneError(err.response?.data?.error || 'Failed to send verification code');
    }
  };

  const handleConfirmVerification = async () => {
    if (otpInput.length !== 6) {
      setPhoneError('Enter the 6-digit code');
      return;
    }

    try {
      await confirmVerification.mutateAsync({ phone: phoneInput, otp: otpInput });
      setShowOtpInput(false);
      setPhoneInput('');
      setOtpInput('');
      toast.success('Phone number verified successfully');
    } catch (err) {
      setPhoneError(err.response?.data?.error || 'Invalid verification code');
    }
  };

  const handleRemovePhone = async () => {
    try {
      await removePhone.mutateAsync();
      toast.success('Phone number removed');
    } catch {
      toast.error('Failed to remove phone number');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-surface-300 border-t-primary-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        <div className="text-center py-8 text-expense-600">
          Failed to load notification preferences
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Summary Reports Card */}
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        {/* Card Header */}
        <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#fef9c3' }}>
            <Bell className="w-5 h-5" style={{ color: '#ca8a04' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-surface-900">Summary Reports</p>
            <p className="text-[11px] text-surface-400">Get periodic financial summaries via email</p>
          </div>
        </div>

        {/* Summary toggles */}
        {summaryTypes.map((type, idx) => {
          const Icon = type.icon;
          const isEnabled = isSummaryEnabled(type.key);

          return (
            <div
              key={type.key}
              className="flex items-center justify-between py-3"
              style={idx < summaryTypes.length - 1 ? { borderBottom: '1px solid #f1f5f9' } : {}}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: isEnabled ? '#dcfce7' : '#f1f5f9' }}
                >
                  <Icon className="w-4 h-4" style={{ color: isEnabled ? '#16a34a' : '#94a3b8' }} />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-surface-900">{type.label}</p>
                  <p className="text-[11px] text-surface-400 mt-0.5">{type.description}</p>
                </div>
              </div>
              <button
                onClick={() => handleToggleSummary(type.key)}
                disabled={updatePreferences.isPending}
                className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ml-3 disabled:opacity-50"
                style={{ backgroundColor: isEnabled ? 'var(--accent-bg)' : '#e2e8f0' }}
                role="switch"
                aria-checked={isEnabled}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
                  isEnabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Phone Verification Card - Only show if SMS or WhatsApp is enabled */}
      {smsEnabled && (
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#ede9fe' }}>
              <Smartphone className="w-5 h-5" style={{ color: '#7c3aed' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-surface-900">Phone Number</p>
              <p className="text-[11px] text-surface-400">Verify for SMS & WhatsApp notifications</p>
            </div>
          </div>

          <div className="pt-3">
            {phoneInfo?.verified ? (
              <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" style={{ color: '#16a34a' }} />
                  <span className="text-[13px] font-semibold" style={{ color: '#166534' }}>
                    {phoneInfo.number}
                  </span>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: '#dcfce7', color: '#16a34a' }}>verified</span>
                </div>
                <button
                  onClick={handleRemovePhone}
                  disabled={removePhone.isPending}
                  className="text-[11px] text-expense-500 font-semibold"
                >
                  {removePhone.isPending ? 'Removing...' : 'Remove'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {!showOtpInput ? (
                  <>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        value={phoneInput}
                        onChange={(e) => setPhoneInput(e.target.value)}
                        placeholder="+919876543210"
                        className="flex-1 min-w-0 px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        onClick={handleSendVerification}
                        disabled={sendVerification.isPending || !phoneInput}
                        className="px-4 py-2.5 rounded-xl font-semibold text-xs transition-all disabled:opacity-50 flex-shrink-0"
                        style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                      >
                        {sendVerification.isPending ? 'Sending...' : 'Verify'}
                      </button>
                    </div>
                    <p className="text-[11px] text-surface-400">
                      Include country code (e.g., +91 for India, +1 for US)
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={otpInput}
                        onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code"
                        className="flex-1 min-w-0 px-3 py-2.5 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-center tracking-widest"
                        maxLength={6}
                      />
                      <button
                        onClick={handleConfirmVerification}
                        disabled={confirmVerification.isPending || otpInput.length !== 6}
                        className="px-4 py-2.5 rounded-xl font-semibold text-xs transition-all disabled:opacity-50 flex-shrink-0"
                        style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                      >
                        {confirmVerification.isPending ? 'Verifying...' : 'Confirm'}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-surface-400">Code sent to {phoneInput}</p>
                      <button
                        onClick={() => { setShowOtpInput(false); setOtpInput(''); setPhoneError(''); }}
                        className="text-[11px] text-primary-600 font-semibold"
                      >
                        Change number
                      </button>
                    </div>
                  </>
                )}

                {phoneError && (
                  <p className="text-[11px] text-expense-600">{phoneError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
        <span className="text-sm flex-shrink-0 mt-0.5">💡</span>
        <span className="text-[11px] leading-relaxed" style={{ color: '#166534' }}>
          Summaries are sent via email when enabled.
          {smsEnabled && ' Verify your phone number to also receive SMS and WhatsApp notifications.'}
        </span>
      </div>
    </div>
  );
}

